// Cierre del pago cuando vence el plazo de 25 minutos: la sesión de Stripe se
// expira (una pestaña vieja ya no puede pagar) y el apartado de Bókun se libera.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

export const expireBookingCheckout = createServerFn({ method: "POST" })
  .inputValidator((input: { idempotencyKey: string }) =>
    z.object({ idempotencyKey: z.string().trim().min(8).max(120) }).parse(input),
  )
  .handler(async ({ data }): Promise<{ expired: boolean }> => {
    const { adminClient } = await import("@/lib/bokun-test/payment-pipeline.server");
    const db = adminClient();
    const { data: row } = await db
      .from("bokun_test_reservations")
      .select(
        "id, environment, bokun_confirmation_code, external_booking_reference, stripe_checkout_session_id, stripe_payment_intent_id, pay_deadline",
      )
      .eq("idempotency_key", data.idempotencyKey)
      .maybeSingle();
    if (!row?.stripe_checkout_session_id || row.stripe_payment_intent_id) return { expired: false };
    // Solo al vencer el plazo (5 s de tolerancia de reloj): nadie puede cortar un pago vigente.
    const deadline = row.pay_deadline ? new Date(row.pay_deadline).getTime() : 0;
    if (!deadline || deadline > Date.now() + 5_000) return { expired: false };
    const { runInBokunEnv, stripeEnvFor } = await import("@/lib/bokun-test/env-context.server");
    const env = row.environment === "live" ? "live" : "test";
    return runInBokunEnv(env, async () => {
      const { createStripeClient } = await import("@/lib/stripe.server");
      const stripe = createStripeClient(stripeEnvFor(env));
      try {
        const session = await stripe.checkout.sessions.retrieve(row.stripe_checkout_session_id!);
        if (session.status === "open") await stripe.checkout.sessions.expire(session.id);
        else if (session.status === "complete") return { expired: false };
      } catch (e) {
        console.error("[bokun-hold] no se pudo expirar la sesión", (e as Error).message);
      }
      try {
        const { abortReservedBooking } = await import("@/lib/bokun-test/booking-api.server");
        await abortReservedBooking({
          bookingRef: row.external_booking_reference,
          confirmationCode: row.bokun_confirmation_code,
        });
      } catch {
        /* Bókun lo libera solo a los 30 min */
      }
      await db
        .from("bokun_test_reservations")
        .update({ status: "CANCELLED", review_reason: "pay_window_expired" })
        .eq("id", row.id);
      return { expired: true };
    });
  });
